import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import {
  AuthorizationStates,
  EventTypes,
  PanelState,
  VisibilityStates,
} from '@racemap/utilities/consts/events';
import { isChildEvent, isGroupEvent } from '@racemap/utilities/functions/utils';
import { Col, Row } from 'antd';
import type { FC } from 'react';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { useEventFieldGenerator } from 'src/lib/customHooks/useEventFieldGenerator';
import { useStore } from '../../../store/reducers';
import { CheckList } from '../../BasicComponents/CheckList';
import { getMetaDataStatus } from '../../utils/helpers';
import { AddOnCardsPanel } from './AddOnsPanel/AddOnCardsPanel';
import { CheckoutPanel } from './CheckoutPanel';
import { EventImagePanel } from './EventImagePanel';
import { GroupBasicTab } from './GroupBasicTab';
import { MetaDataPanel } from './MetaDataPanel/MetaDataPanel';
import { StartUpPanel } from './StartUpPanel';
import { TrackPanel } from './TrackPanel';
import { VisibilityPanel } from './VisibilityPanel';

export const BasicTab: FC = () => {
  const event = useCurrentEvent();
  const fieldGenerator = useEventFieldGenerator(event);
  const events = useStore((s) => s.events.items);
  const isSafemap = event?.type === EventTypes.SAFEMAP;

  if (event == null || fieldGenerator == null) return <></>;

  if (isGroupEvent(event)) {
    return <GroupBasicTab />;
  }

  const checkpoints = [];
  const hasMapInfo = event.geo?.features != null ? event.geo?.features.length > 0 : 0;
  const hasParticipants = event?.startersCount != null && event?.startersCount > 0;

  const startUpPanelStatus = hasParticipants ? PanelState.SUCCESS : PanelState.NONE;
  const metaDataStatus = getMetaDataStatus(event);
  const eventImageStatus = event.images != null ? PanelState.SUCCESS : PanelState.NONE;
  const trackStatus = hasMapInfo ? PanelState.SUCCESS : PanelState.NONE;
  const checkOutStatus =
    event.authorization !== AuthorizationStates.NONE ? PanelState.SUCCESS : PanelState.NONE;
  const visibilityStatus =
    event.visibility === VisibilityStates.ARCHIVED
      ? PanelState.PRIMARY
      : event.visibility === VisibilityStates.DRAFT
        ? PanelState.NONE
        : PanelState.SUCCESS;

  const startUpObj = {
    status: startUpPanelStatus,
    key: 'startUp',
    checkpoint: <StartUpPanel status={startUpPanelStatus} />,
  };
  const metaDataObj = {
    status: metaDataStatus,
    key: 'metaData',
    checkpoint: <MetaDataPanel />,
  };
  const eventImageObj = {
    status: eventImageStatus,
    key: 'eventImage',
    checkpoint: <EventImagePanel />,
  };
  const trackObj = {
    status: trackStatus,
    key: 'track',
    checkpoint: <TrackPanel status={trackStatus} />,
  };
  const checkOutObj = {
    status: checkOutStatus,
    key: 'checkout',
    checkpoint: <CheckoutPanel status={checkOutStatus} />,
  };
  const visibilityObj = {
    status: visibilityStatus,
    key: 'visibility',
    checkpoint: (
      <VisibilityPanel
        {...fieldGenerator('visibility')}
        status={visibilityStatus}
        authorized={event.authorization !== AuthorizationStates.NONE}
        possibleStates={Object.values(VisibilityStates).filter(
          (e) => e !== VisibilityStates.PUBLIC && e !== VisibilityStates.ARCHIVED,
        )}
      />
    ),
  };

  if (!isSafemap && (event.startersCount == null || event.startersCount < 10)) {
    checkpoints.push(startUpObj);
  }
  checkpoints.push(metaDataObj);
  checkpoints.push(eventImageObj);
  checkpoints.push(trackObj);
  if (checkOutStatus !== PanelState.SUCCESS) {
    checkpoints.push(checkOutObj);
  }
  if (event.authorization !== AuthorizationStates.NONE && !isChildEvent(event) && !isSafemap) {
    checkpoints.push(visibilityObj);
  }

  return (
    <div>
      <Row style={{ display: 'flex', justifyContent: 'center', margin: '15px auto' }}>
        <Col md={20} xs={20}>
          {events.size < 2 && !isSafemap && (
            <Row>
              <Col
                style={{
                  width: '600px',
                  margin: '0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {event.authorization === AuthorizationStates.NONE && (
                  <>
                    <h2>Create your own live tracking</h2>
                    <Subtitle>Use our free trial to test live tracking.</Subtitle>
                  </>
                )}
                <Subtitle>
                  Customize live tracking for your event. All settings and every information can be
                  changed at any time.
                </Subtitle>
              </Col>
            </Row>
          )}

          <Row>
            <Col style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <CheckList checkpoints={checkpoints} />
            </Col>
          </Row>
        </Col>
      </Row>
      <section>
        <Row style={{ display: 'flex', justifyContent: 'center' }}>
          <Col md={20} xs={22}>
            <AddOnCardsPanel />
          </Col>
        </Row>
      </section>
    </div>
  );
};

const Subtitle = styled.div`
  font-size: large;
  color: ${RacemapColors.DarkGray};
`;
